// src/adminCalendar/bookingActions.js
// LOOP R0.2.5 — Reservation Action Layer
// Shared reservation action helpers used by BookingCreateModal, EventViewModal and AdminBookingCalendar.
// This intentionally centralizes action payloads without doing a risky UI rewrite.

import { getFunctions, httpsCallable } from "firebase/functions";
import {
  buildCanonicalBookingPatch,
  buildBookingPaymentPatch,
  buildBookingSchedulePatch,
  buildBookingStatusPatch,
} from "../utils/bookingEngine";

function safeString(value, fallback = "") {
  const out = String(value ?? "").trim();
  return out || fallback;
}

function safeIsoNow() {
  return new Date().toISOString();
}

function actorPatch(actor = {}, prefix = "modified") {
  const uid = actor?.uid || actor?.userUid || actor?.id || null;
  const email = actor?.email || actor?.userEmail || null;
  return {
    [`${prefix}ByUid`]: uid || null,
    [`${prefix}ByEmail`]: email || null,
  };
}

export function buildBookingActionPatch({ booking = {}, patch = {}, action = "booking_update", actor = null } = {}) {
  const canonical = buildCanonicalBookingPatch(booking, patch, {
    defaultBookingSource: booking.bookingSource || booking.source || action,
  });
  return {
    ...canonical,
    bookingAction: action,
    bookingActionUpdatedAtClient: safeIsoNow(),
    ...(actor ? actorPatch(actor, "modified") : {}),
  };
}

export function buildBookingStatusActionPatch({
  booking = {},
  nextStatus,
  source = "status_update",
  actor = null,
  statusMeta = null,
  previousStatus = null,
  basePayload = {},
} = {}) {
  const normalized = safeString(nextStatus || basePayload.status || booking.status || "confirmed", "confirmed");
  const previous = safeString(previousStatus || booking.status || booking.confirmationStatus || "confirmed", "confirmed");
  const canonical = buildBookingStatusPatch(booking, normalized);
  const label = statusMeta?.label || normalized;
  const tone = statusMeta?.tone || "neutral";
  return {
    ...canonical,
    ...basePayload,
    status: normalized,
    bookingStatus: normalized,
    confirmationStatus: normalized,
    statusLabel: label,
    statusTone: tone,
    statusWorkflow: {
      ...(basePayload.statusWorkflow || {}),
      previousStatus: previous,
      previousLabel: basePayload.statusWorkflow?.previousLabel || previous,
      currentStatus: normalized,
      currentLabel: label,
      currentTone: tone,
      source,
      changedAtClient: safeIsoNow(),
      changedByUid: actor?.uid || null,
      changedByEmail: actor?.email || null,
    },
    statusHistoryLast: {
      from: previous,
      to: normalized,
      label,
      source,
      changedAtClient: safeIsoNow(),
      changedByUid: actor?.uid || null,
      changedByEmail: actor?.email || null,
    },
    bookingAction: "status_update",
    bookingActionSource: source,
    ...(actor ? actorPatch(actor, "modified") : {}),
  };
}

export function buildBookingPaymentActionPatch({ booking = {}, paymentPayload = {}, actor = null } = {}) {
  const canonical = buildBookingPaymentPatch(booking, paymentPayload);
  const status = paymentPayload?.paymentStatus || canonical.paymentStatus || booking.paymentStatus || booking.payment?.status || "pending";
  const coverageType = paymentPayload?.paymentCoverageType || paymentPayload?.coverageType || canonical.paymentCoverageType || booking.paymentCoverageType || booking.payment?.coverageType || (String(status).toLowerCase() === "covered" ? (paymentPayload?.paymentMethod || booking.paymentMethod || "membership") : "cash");
  const workerPayoutStatus = paymentPayload?.workerPayoutStatus || canonical.workerPayoutStatus || booking.workerPayoutStatus || booking.payment?.workerPayoutStatus || "pending";
  return {
    ...canonical,
    ...(paymentPayload || {}),
    paymentStatus: status,
    workerPayoutStatus,
    payment: {
      ...(booking.payment || {}),
      ...(paymentPayload.payment || {}),
      status,
      method: paymentPayload.paymentMethod ?? paymentPayload.payment?.method ?? canonical.paymentMethod ?? booking.paymentMethod ?? booking.payment?.method ?? "cash",
      coverageType,
      paymentCoverageType: coverageType,
      amountCharged: paymentPayload.amountCharged ?? paymentPayload.paidAmount ?? canonical.amountCharged ?? 0,
      paidAmount: paymentPayload.paidAmount ?? paymentPayload.amountCharged ?? canonical.paidAmount ?? 0,
      serviceCollectedAmount: paymentPayload.serviceCollectedAmount ?? paymentPayload.amountCharged ?? canonical.serviceCollectedAmount ?? 0,
      tipAmount: paymentPayload.tipAmount ?? canonical.tipAmount ?? booking.tipAmount ?? booking.payment?.tipAmount ?? 0,
      tipPaymentMethod: paymentPayload.tipPaymentMethod ?? paymentPayload.payment?.tipPaymentMethod ?? canonical.tipPaymentMethod ?? booking.tipPaymentMethod ?? booking.payment?.tipPaymentMethod ?? "unknown",
      coveredAmount: paymentPayload.coveredAmount ?? canonical.coveredAmount ?? booking.coveredAmount ?? booking.payment?.coveredAmount ?? 0,
      commissionBaseAmount: paymentPayload.commissionBaseAmount ?? canonical.commissionBaseAmount ?? booking.commissionBaseAmount ?? booking.payment?.commissionBaseAmount ?? 0,
      workerPayoutStatus,
    },
    bookingAction: "payment_update",
    paymentActionUpdatedAtClient: safeIsoNow(),
    ...(actor ? actorPatch(actor, "paymentUpdated") : {}),
    ...(actor ? actorPatch(actor, "modified") : {}),
  };
}

export function buildBookingScheduleActionPatch({ start, end, durationMinutes, employee = null, resourceId = null, actor = null } = {}) {
  return {
    ...buildBookingSchedulePatch({ start, end, durationMinutes, employee, resourceId }),
    bookingAction: "schedule_update",
    scheduleActionUpdatedAtClient: safeIsoNow(),
    ...(actor ? actorPatch(actor, "modified") : {}),
  };
}

export async function reprocessBookingLoyaltyAction({ db, businessId, bookingId } = {}) {
  if (!businessId || !bookingId) {
    throw new Error("Falta businessId o bookingId para reprocesar fidelidad.");
  }
  const firebaseApp = db?.app || db?._app;
  const functions = firebaseApp ? getFunctions(firebaseApp) : getFunctions();
  const reprocess = httpsCallable(functions, "reprocessBookingLoyalty");
  return reprocess({ businessId, bookingId });
}


export async function reverseLoyaltyRedemptionAction({ db, businessId, bookingId, reason = "" } = {}) {
  if (!businessId || !bookingId) {
    throw new Error("Falta businessId o bookingId para deshacer fidelidad.");
  }
  const firebaseApp = db?.app || db?._app;
  const functions = firebaseApp ? getFunctions(firebaseApp) : getFunctions();
  const reverse = httpsCallable(functions, "reverseLoyaltyRedemption");
  return reverse({ businessId, bookingId, reason });
}

export function summarizeReverseLoyaltyRedemptionResult(result) {
  const data = result?.data || {};
  if (data.alreadyReversed && data.bookingCleaned) return "La redención ya estaba deshecha; limpié la reserva para quitar Fidelidad/Cubierto.";
  if (data.alreadyReversed) return "La redención de fidelidad ya estaba deshecha.";
  if (data.ok) {
    const reward = data.rewardLabel ? ` · ${data.rewardLabel}` : "";
    return `Fidelidad deshecha correctamente${reward}. La recompensa volvió al cliente y la reserva quedó pendiente de pago.`;
  }
  return "Fidelidad deshecha.";
}


export function summarizeReprocessResult(result) {
  const processedCount = Array.isArray(result?.data?.processed) ? result.data.processed.length : 0;
  const skippedCount = Array.isArray(result?.data?.skipped) ? result.data.skipped.length : 0;
  const reason = result?.data?.reason || "";
  if (processedCount > 0) {
    const labels = result?.data?.processed
      ?.map((item) => item?.earnedLabel || (item?.programType === "points" ? `${item?.amount || 0} puntos` : `${item?.amount || 0} sellos`))
      ?.filter(Boolean)
      ?.slice(0, 3)
      ?.join(" · ");
    return `Fidelidad reprocesada: ${processedCount} programa${processedCount === 1 ? "" : "s"} actualizado${processedCount === 1 ? "" : "s"}${labels ? ` (${labels})` : ""}.`;
  }
  if (reason) return `No se aplicó fidelidad: ${reason}.`;
  const skippedReasons = result?.data?.skipped
    ?.map((item) => item?.programName ? `${item.programName}: ${item.reason || "omitido"}` : item?.reason)
    ?.filter(Boolean)
    ?.slice(0, 3)
    ?.join(" · ");
  return `Fidelidad revisada. Procesados: ${processedCount}. Omitidos: ${skippedCount}${skippedReasons ? ` (${skippedReasons})` : ""}.`;
}


export async function processBookingMembershipBenefitAction({ businessId, bookingId } = {}) {
  const fn = httpsCallable(getFunctions(), "processBookingMembershipBenefit");
  const res = await fn({ businessId, bookingId });
  return res?.data || {};
}

export async function reprocessBookingMembershipBenefitAction({ businessId, bookingId } = {}) {
  const fn = httpsCallable(getFunctions(), "reprocessBookingMembershipBenefit");
  const res = await fn({ businessId, bookingId });
  return res?.data || {};
}


export async function renewCustomerMembershipAction({ businessId, customerId, customerMembershipId, membershipId, reason, resetCredits = true, startDate = null, endDate = null } = {}) {
  const fn = httpsCallable(getFunctions(), "renewCustomerMembership");
  const res = await fn({
    businessId,
    customerId,
    customerMembershipId: customerMembershipId || membershipId,
    membershipId: membershipId || customerMembershipId,
    reason,
    resetCredits,
    startDate,
    endDate,
  });
  return res?.data || {};
}

export function summarizeMembershipRenewalResult(result = {}) {
  if (!result || result.ok === false) return "No pude renovar la membresía.";
  const included = result.creditsIncluded ?? result.creditsAfter ?? "—";
  return `Membresía renovada. Nuevo ciclo activo con ${included} crédito${Number(included) === 1 ? "" : "s"} disponibles.`;
}



export async function expireCustomerMembershipsNowAction({ businessId, dryRun = false, limit = 300 } = {}) {
  const fn = httpsCallable(getFunctions(), "expireCustomerMembershipsNow");
  const res = await fn({ businessId, dryRun, limit });
  return res?.data || {};
}

export function summarizeMembershipExpirationResult(result = {}) {
  if (!result || result.ok === false) return "No pude revisar vencimientos de membresías.";
  const expired = Number(result.expired || 0);
  const checked = Number(result.checked || 0);
  if (result.dryRun) return `Revisión lista: ${expired} membresía${expired === 1 ? "" : "s"} vencería${expired === 1 ? "" : "n"}. Revisadas: ${checked}.`;
  if (expired > 0) return `Vencimientos actualizados: ${expired} membresía${expired === 1 ? "" : "s"} marcada${expired === 1 ? "" : "s"} como vencida${expired === 1 ? "" : "s"}.`;
  return `Membresías revisadas. No había vencimientos pendientes. Revisadas: ${checked}.`;
}

export async function adjustCustomerMembershipCreditsAction({ businessId, customerId, customerMembershipId, membershipId, deltaCredits, reason, bookingId = null } = {}) {
  const fn = httpsCallable(getFunctions(), "adjustCustomerMembershipCredits");
  const res = await fn({ businessId, customerId, customerMembershipId: customerMembershipId || membershipId, membershipId: membershipId || customerMembershipId, deltaCredits, reason, bookingId });
  return res?.data || {};
}

export function summarizeMembershipCreditAdjustmentResult(result = {}) {
  if (!result || result.ok === false) return "No pude ajustar los créditos de la membresía.";
  const delta = Number(result.deltaCredits || 0);
  const direction = delta > 0 ? "devuelto" : "descontado";
  const amount = Math.abs(delta);
  return `Ajuste registrado: ${amount} crédito${amount === 1 ? "" : "s"} ${direction}. Quedan ${result.creditsAfter ?? "—"}.`;
}


export function summarizeMembershipBenefitResult(result = {}) {
  if (!result || result.ok === false) return "No pude procesar el beneficio de membresía.";
  if (result.reverted) return `Ajuste de membresía registrado (${result.creditsRestored || 1}).`;
  if (result.skipped) {
    const reason = result.reason || "skipped";
    const map = {
      already_processed: "El beneficio de membresía ya estaba aplicado.",
      no_membership_benefit_on_booking: "Esta reserva no tiene membresía aplicada.",
      booking_not_paid_or_completed: "Primero marca la reserva como pagada o completada.",
      missing_membership_id: "Falta la membresía del cliente en esta reserva.",
      membership_not_found: "No encontré la membresía del cliente.",
      membership_not_active: "La membresía no está activa.",
      service_not_eligible: "El servicio no aplica para esta membresía.",
      not_enough_credits: "La membresía no tiene créditos suficientes.",
      booking_cancelled_like: "La reserva está cancelada/no aplica.",
      booking_cancelled_like_manual_restore_only: "Reserva cancelada/no-show: Loop no restaura créditos automáticamente. Ajuste manual desde CRM si aplica.",
    };
    return map[reason] || `No se aplicó membresía: ${reason}`;
  }
  if (result.benefitType === "credit") return `Membresía aplicada: ${result.creditsUsed || 1} crédito usado. Quedan ${result.creditsAfter ?? "—"}.`;
  if (result.benefitType === "discount") return `Membresía aplicada: ${result.discountPercent || 0}% de descuento.`;
  return "Beneficio de membresía aplicado.";
}


export async function getCustomerMembershipActivityAction({ businessId, customerId, customerMembershipId = null, membershipId = null, limit = 60 } = {}) {
  const fn = httpsCallable(getFunctions(), "getCustomerMembershipActivity");
  const res = await fn({ businessId, customerId, customerMembershipId: customerMembershipId || membershipId || null, membershipId: membershipId || customerMembershipId || null, limit });
  return res?.data || {};
}

export function summarizeMembershipActivityItem(item = {}) {
  if (!item) return "Actividad de membresía";
  const title = item.title || item.description || item.activityType || item.type || "Actividad de membresía";
  const after = item.creditsAfter === null || item.creditsAfter === undefined ? "" : ` · quedan ${item.creditsAfter}`;
  return `${title}${after}`;
}
